export default function AddTokenGuidePage() {
  return (
    <div className="guide-wrap">
      <GuideStyles />
      <div className="guide-doc">

        <div className="guide-header">
          <div className="guide-eyebrow">BESC Bridge · Admin Reference</div>
          <h1 className="guide-h1">How to Add a New Bridge Asset</h1>
          <p className="guide-lead">Admin Panel → Tokens tab → Add Token</p>
        </div>

        <nav className="guide-toc">
          <div className="guide-toc-title">Contents</div>
          <ol className="guide-toc-list">
            {[
              "Before you start — what you need",
              "Deploy vs. Register",
              "Asset types — choose your setup",
              "Basic token info",
              "Source chain & contract address",
              "Tax configuration",
              "Tax recipients",
              "Auto-LP (liquidity building)",
              "Rewards token payout (ERC20)",
              "Auto-convert (swapback)",
              "Bridge fee & notes",
              "After deploy",
              "Quick-reference — failure reasons",
            ].map((item, i) => (
              <li key={i}><span className="guide-toc-n">{String(i + 1).padStart(2, "0")}</span>{item}</li>
            ))}
          </ol>
        </nav>

        {/* ── 01 ── */}
        <h2 className="guide-h2">01 — Before You Start</h2>
        <p className="guide-p">Have the following ready before opening the form. Missing any of these will block you mid-way.</p>
        <div className="guide-grid2">
          {[
            ["Token details", "Name, ticker symbol, and decimal places of the source token"],
            ["Source chain", "Which chain the token lives on — BSC, ETH, SOL, XRP, RH, or BESC"],
            ["Source contract address", "The token's contract address on its home chain"],
            ["Tax wallets (if taxed)", "BESC addresses for each recipient + their percentage share"],
            ["DEX router (if swapback)", "Uniswap V2-compatible router address on BESC"],
            ["Rewards token (if ERC20 rewards)", "Contract address of the payout token on BESC"],
          ].map(([label, desc]) => (
            <div key={label} className="guide-card">
              <div className="guide-card-label">{label}</div>
              <div className="guide-card-val">{desc}</div>
            </div>
          ))}
        </div>

        {/* ── 02 ── */}
        <h2 className="guide-h2">02 — Deploy vs. Register</h2>
        <div className="guide-card guide-card--block">
          <div className="guide-card-label">Deploy new wrapped contract</div>
          <div className="guide-card-val">Use this when the project does <strong>not</strong> already have a wrapped token on BESC. The system deploys a fresh contract with all your tax and swapback settings baked in, then registers it. This is the most common path.</div>
        </div>
        <div className="guide-card guide-card--block" style={{ marginTop: 8 }}>
          <div className="guide-card-label">Register existing wrapped contract</div>
          <div className="guide-card-val">Use this when a wrapped contract is <strong>already deployed</strong> on BESC. You provide the existing address only — tax/swapback settings are not configured here because they already live on-chain.</div>
        </div>

        {/* ── 03 ── */}
        <h2 className="guide-h2">03 — Asset Types — Choose Your Setup</h2>
        <p className="guide-p">Every token you add falls into one of these configurations. Know which one applies before you open the form — it determines which fields you need to fill.</p>

        <div className="guide-scenario">
          <div className="guide-scenario-badge guide-scenario-badge--plain">Type A</div>
          <div className="guide-scenario-title">No-Tax Bridge Token</div>
          <p className="guide-p">A simple wrapped asset with zero tax. Bridging mints/burns 1-to-1 (minus the bridge fee). Nothing else needed.</p>
          <div className="guide-scenario-fields">
            <span className="guide-field-pill guide-field-pill--req">Basic info</span>
            <span className="guide-field-pill guide-field-pill--req">Source chain + address</span>
            <span className="guide-field-pill guide-field-pill--req">Bridge fee</span>
          </div>
          <div className="guide-callout guide-callout--tip">Leave all tax fields at 0. Skip tax recipients, rewards token, and swapback entirely.</div>
        </div>

        <div className="guide-scenario">
          <div className="guide-scenario-badge guide-scenario-badge--tax">Type B</div>
          <div className="guide-scenario-title">Tax Token — Direct Wallet Distribution</div>
          <p className="guide-p">Collects a tax on transfers and sends it <strong>directly to recipient wallets</strong> in the tax token itself — no conversion, no DEX. Recipients accumulate the project token in their wallets.</p>
          <div className="guide-scenario-fields">
            <span className="guide-field-pill guide-field-pill--req">Basic info</span>
            <span className="guide-field-pill guide-field-pill--req">Tax % (transfer / buy / sell)</span>
            <span className="guide-field-pill guide-field-pill--req">Tax recipients + shares</span>
          </div>
          <div className="guide-callout guide-callout--tip">Do NOT enable swapback. Leave the rewards token blank. Tax flows straight to wallets as-is.</div>
          <div className="guide-example">
            <div className="guide-example-label">Example setup</div>
            <div className="guide-example-body">
              Transfer Tax: 3% &nbsp;·&nbsp; Sell Tax: 5%<br />
              Recipients: Dev wallet 50%, Marketing wallet 30%, Team wallet 20%<br />
              Swapback: Off &nbsp;·&nbsp; Rewards token: None
            </div>
          </div>
        </div>

        <div className="guide-scenario">
          <div className="guide-scenario-badge guide-scenario-badge--buyback">Type C</div>
          <div className="guide-scenario-title">Buyback Token — Tax Converts to Native BESC</div>
          <p className="guide-p">Collects tax, accumulates it in the contract, then automatically swaps it through a DEX to <strong>native BESC</strong> when the threshold is hit. The BESC is distributed to recipient wallets. Used for buyback-and-burn or BESC rewards.</p>
          <div className="guide-scenario-fields">
            <span className="guide-field-pill guide-field-pill--req">Tax %</span>
            <span className="guide-field-pill guide-field-pill--req">Tax recipients + shares</span>
            <span className="guide-field-pill guide-field-pill--req">Auto-convert: ON</span>
            <span className="guide-field-pill guide-field-pill--req">DEX router</span>
            <span className="guide-field-pill guide-field-pill--req">Output token (WBESC)</span>
            <span className="guide-field-pill guide-field-pill--req">Threshold</span>
          </div>
          <div className="guide-callout guide-callout--info">Leave the <strong>Rewards Token</strong> field blank — the output is native BESC, not an ERC20. Set Output Token to the WBESC contract address on BESC.</div>
          <div className="guide-example">
            <div className="guide-example-label">Example setup</div>
            <div className="guide-example-body">
              Sell Tax: 4%<br />
              Recipients: Dev wallet 40%, Buyback wallet 60%<br />
              Swapback: ON &nbsp;·&nbsp; Payout: Native BESC<br />
              Output Token: WBESC address on BESC<br />
              Router: BescSwap router &nbsp;·&nbsp; Threshold: 5000
            </div>
          </div>
        </div>

        <div className="guide-scenario">
          <div className="guide-scenario-badge guide-scenario-badge--rewards">Type D</div>
          <div className="guide-scenario-title">Rewards Token — Tax Converts to ERC20 for Holders</div>
          <p className="guide-p">Collects tax, accumulates it, then automatically swaps to a <strong>specific ERC20 token</strong> (e.g. WBNB, USDT) and distributes to holders. This is what powers "reflections" or "dividend" tokens where holders earn a different asset.</p>
          <div className="guide-scenario-fields">
            <span className="guide-field-pill guide-field-pill--req">Tax %</span>
            <span className="guide-field-pill guide-field-pill--req">Tax recipients + shares</span>
            <span className="guide-field-pill guide-field-pill--req">Rewards Token address on BESC</span>
            <span className="guide-field-pill guide-field-pill--req">Auto-convert: ON</span>
            <span className="guide-field-pill guide-field-pill--req">DEX router</span>
            <span className="guide-field-pill guide-field-pill--req">Threshold</span>
          </div>
          <div className="guide-callout guide-callout--info">Fill in the <strong>Rewards Token</strong> field — this auto-wires ERC20 mode. The rewards token IS the output of the swap. Do not also fill in Output Token (it's hidden when rewards token is set).</div>
          <div className="guide-example">
            <div className="guide-example-label">Example setup — WBNB rewards</div>
            <div className="guide-example-body">
              Buy Tax: 2% &nbsp;·&nbsp; Sell Tax: 5%<br />
              Recipients: Dev wallet 30%, Rewards wallet 70%<br />
              Rewards Token: WBNB contract on BESC (0x…)<br />
              Swapback: ON &nbsp;·&nbsp; Router: BescSwap router<br />
              Threshold: 1000
            </div>
          </div>
        </div>

        <div className="guide-scenario">
          <div className="guide-scenario-badge guide-scenario-badge--lp">Type E</div>
          <div className="guide-scenario-title">Auto-LP Token — Tax Builds Liquidity</div>
          <p className="guide-p">A portion of the tax is used to <strong>automatically add liquidity</strong> to a DEX pair on BESC. The contract splits the LP share: half stays as the project token, half swaps to the paired token (usually WBESC), then both are added as LP. The LP tokens go to a designated wallet.</p>
          <div className="guide-scenario-fields">
            <span className="guide-field-pill guide-field-pill--req">Tax %</span>
            <span className="guide-field-pill guide-field-pill--req">Tax recipients — include an Auto-LP wallet</span>
            <span className="guide-field-pill guide-field-pill--req">LP Paired Token (WBESC)</span>
            <span className="guide-field-pill guide-field-pill--req">LP Tax Share %</span>
            <span className="guide-field-pill guide-field-pill--req">Auto-convert: ON</span>
            <span className="guide-field-pill guide-field-pill--req">DEX router + Threshold</span>
          </div>
          <div className="guide-callout guide-callout--warn">Add a recipient with the <strong>Auto-LP</strong> purpose label — that's what makes the LP fields appear. The LP Tax Share % is separate from recipient shares; it carves out that portion of the total tax before distributing to recipients.</div>
          <div className="guide-example">
            <div className="guide-example-label">Example setup</div>
            <div className="guide-example-body">
              Sell Tax: 6%<br />
              Recipients: Dev wallet 50%, LP wallet 50% (purpose: Auto-LP)<br />
              LP Paired Token: WBESC on BESC &nbsp;·&nbsp; LP Tax Share: 3%<br />
              Swapback: ON &nbsp;·&nbsp; Router: BescSwap router &nbsp;·&nbsp; Threshold: 2000
            </div>
          </div>
        </div>

        <div className="guide-scenario">
          <div className="guide-scenario-badge guide-scenario-badge--full">Type F</div>
          <div className="guide-scenario-title">Full Setup — Dev + Buyback/Rewards + Auto-LP</div>
          <p className="guide-p">The most complex configuration. Part of the tax goes directly to a dev wallet, part converts to ERC20 rewards for holders, and part builds liquidity — all from a single buy/sell tax.</p>
          <div className="guide-scenario-fields">
            <span className="guide-field-pill guide-field-pill--req">Buy Tax % + Sell Tax %</span>
            <span className="guide-field-pill guide-field-pill--req">Recipients: Dev + Rewards + LP wallets</span>
            <span className="guide-field-pill guide-field-pill--req">Rewards Token address on BESC</span>
            <span className="guide-field-pill guide-field-pill--req">LP Paired Token + LP Tax Share %</span>
            <span className="guide-field-pill guide-field-pill--req">Auto-convert: ON + Router + Threshold</span>
          </div>
          <div className="guide-example">
            <div className="guide-example-label">Example setup — 5% buy / 8% sell</div>
            <div className="guide-example-body">
              Buy Tax: 5% &nbsp;·&nbsp; Sell Tax: 8%<br />
              Recipients: Dev 25%, Rewards holder 50%, LP wallet 25% (Auto-LP)<br />
              Rewards Token: WBNB on BESC (0x…)<br />
              LP Paired Token: WBESC on BESC &nbsp;·&nbsp; LP Tax Share: 2%<br />
              Swapback: ON &nbsp;·&nbsp; Router: BescSwap &nbsp;·&nbsp; Threshold: 5000
            </div>
          </div>
        </div>

        {/* ── 04 ── */}
        <h2 className="guide-h2">04 — Basic Token Info</h2>
        <div className="guide-steps">
          <Step n={1} title="Token Name">The full display name shown in the bridge UI. Example: <Code>Wrapped ETH</Code>, <Code>PepeToken</Code>.</Step>
          <Step n={2} title="Symbol">The ticker, usually 3–6 characters. The internal API token is derived from it — e.g. <Code>PEPE</Code> becomes <Code>WPEPE</Code>. Cannot conflict with an existing symbol.</Step>
          <Step n={3} title="Decimals">
            <p className="guide-p">Must match exactly what the original contract uses. Most ERC-20s use <Code>18</Code>. USDC / XRP use <Code>6</Code>. SOL SPL tokens vary — confirm with the project team if unsure.</p>
            <div className="guide-callout guide-callout--warn">Getting decimals wrong causes incorrect bridge amounts and cannot be changed after deploy without redeploying the contract.</div>
          </Step>
        </div>

        {/* ── 05 ── */}
        <h2 className="guide-h2">05 — Source Chain & Contract Address</h2>
        <p className="guide-p">Select the chain where the original token lives, then enter its contract address. Address format varies by chain — the form validates automatically.</p>
        <div className="guide-chain-grid">
          {[
            ["ETH / BSC / RH / BESC", "0x + 40 hex chars"],
            ["SOL", "Base58 · 32–44 chars"],
            ["XRP", "Starts with r · 25–35 chars"],
          ].map(([name, fmt]) => (
            <div key={name} className="guide-chain-chip">
              <span className="guide-chain-name">{name}</span>
              <span className="guide-chain-fmt">{fmt}</span>
            </div>
          ))}
        </div>
        <div className="guide-callout guide-callout--tip">Copy the address directly from the block explorer for that chain — never type it by hand.</div>
        <div className="guide-addr">
          BSC/ETH/RH/BESC → 0x2170Ed0880ac9A755fd29B2688956BD959F933F8{"\n"}
          SOL              → So11111111111111111111111111111111111111112{"\n"}
          XRP              → rHb9CJAWyB4rj91VRWn96DkukG4bwdtyTh
        </div>

        {/* ── 06 ── */}
        <h2 className="guide-h2">06 — Tax Configuration</h2>
        <p className="guide-p">Tax is optional. If no tax, leave everything at 0 and skip to Step 11 (Bridge Fee).</p>
        <table className="guide-table">
          <thead><tr><th>Field</th><th>What it means</th></tr></thead>
          <tbody>
            <Tr name="Transfer Tax %" opt>Taken on every wallet-to-wallet transfer, regardless of buy or sell. Used for a flat tax on all movement including DEX trades.</Tr>
            <Tr name="Buy Tax %" opt>Only taken when buying through a registered AMM pair (DEX). Requires AMM pairs to be registered after deploy in the Manage view.</Tr>
            <Tr name="Sell Tax %" opt>Only taken when selling through a registered AMM pair. Can differ from buy tax — most projects charge a higher sell tax.</Tr>
          </tbody>
        </table>
        <div className="guide-callout guide-callout--info">
          <strong>How the modes combine:</strong><br />
          Transfer Tax only → applies to all transactions including DEX<br />
          Buy + Sell Tax only → transfer-to-wallet is tax-free; DEX buys/sells are taxed at their own rate<br />
          Transfer + Buy/Sell → base rate on all transfers; DEX trades use buy/sell rates instead
        </div>
        <div className="guide-callout guide-callout--warn">All tax recipients must add up to exactly 100% of the collected tax. The system validates this before deploying.</div>

        {/* ── 07 ── */}
        <h2 className="guide-h2">07 — Tax Recipients</h2>
        <p className="guide-p">When any tax is enabled, specify who receives it and in what proportions. Add one row per recipient. Up to 10 recipients allowed.</p>
        <table className="guide-table">
          <thead><tr><th>Column</th><th>What to enter</th></tr></thead>
          <tbody>
            <Tr name="Wallet" req>A BESC (0x…) address that receives this share of the tax.</Tr>
            <Tr name="Share %" req>This wallet's percentage of the total tax. All rows must sum to exactly 100.</Tr>
            <Tr name="Purpose" opt>
              Internal label only — does not affect on-chain behavior.<br />
              <span style={{ color: "#b8c4d4" }}>
                <strong>Team / Dev / Marketing</strong> — direct wallet recipients<br />
                <strong>Rewards</strong> — wallet that holds accumulated rewards before distribution<br />
                <strong>Auto-LP</strong> — triggers the LP paired token and LP tax share fields below<br />
                <strong>Buyback</strong> — wallet receiving buyback proceeds<br />
                <strong>Other</strong> — anything else
              </span>
            </Tr>
          </tbody>
        </table>
        <div className="guide-callout guide-callout--info">
          Tax distribution behavior depends on whether swapback is on or off:<br />
          <strong>Swapback OFF</strong> → tax tokens sent directly to wallets as the project token<br />
          <strong>Swapback ON</strong> → tax accumulates in contract, swaps to output token, then distributes to wallets
        </div>

        {/* ── 08 ── */}
        <h2 className="guide-h2">08 — Auto-LP</h2>
        <p className="guide-p">Auto-LP uses part of the collected tax to automatically add liquidity to a DEX pair on BESC. These fields appear when any recipient has the <strong>Auto-LP</strong> purpose label.</p>
        <table className="guide-table">
          <thead><tr><th>Field</th><th>What to enter</th></tr></thead>
          <tbody>
            <Tr name="LP Paired Token" req>The other token in the DEX pair — usually WBESC. Enter its contract address on BESC.</Tr>
            <Tr name="LP Tax Share %" req>
              Percentage of total collected tax routed to auto-LP. Max 50%.<br />
              The contract handles it automatically: splits this portion in half — one half stays as the project token, the other half swaps to the paired token — then adds both as liquidity. LP tokens go to the Auto-LP recipient wallet.
            </Tr>
          </tbody>
        </table>
        <div className="guide-callout guide-callout--warn">Auto-LP requires swapback to be enabled — the contract must be able to swap to acquire the paired token side of the LP.</div>

        {/* ── 09 ── */}
        <h2 className="guide-h2">09 — Rewards Token Payout (ERC20)</h2>
        <div className="guide-highlight">
          <div className="guide-highlight-title">Rewards Token Contract Address on BESC</div>
          <p className="guide-p" style={{ marginTop: 6 }}>This is the specific ERC20 token that holders receive when the contract distributes rewards. Examples: WBNB, USDT, BUSD, or any other token deployed on BESC.</p>
          <p className="guide-p"><strong>Leave blank</strong> if paying rewards in native BESC, or if there are no rewards (direct distribution or no tax).</p>
        </div>
        <p className="guide-p">When you fill this in, the system automatically switches the auto-convert section to ERC20 mode — you do not need to manually change anything else.</p>

        <table className="guide-table">
          <thead><tr><th>Situation</th><th>Rewards Token field</th></tr></thead>
          <tbody>
            <tr><td>No tax at all</td><td style={{ color: "#7a8b9a" }}>Leave blank</td></tr>
            <tr><td>Tax goes directly to wallets (no swapback)</td><td style={{ color: "#7a8b9a" }}>Leave blank</td></tr>
            <tr><td>Swapback to native BESC (buyback)</td><td style={{ color: "#7a8b9a" }}>Leave blank — set Output Token in swapback section instead</td></tr>
            <tr><td>Swapback to an ERC20 rewards token</td><td style={{ color: "#4ecb71", fontWeight: 600 }}>Fill in the ERC20 contract address on BESC</td></tr>
          </tbody>
        </table>

        <div className="guide-callout guide-callout--warn">The rewards token address must be the version deployed <strong>on BESC</strong>, not on BSC or ETH. If the project uses WBNB as rewards, get the WBNB contract address that exists on BESC specifically.</div>

        {/* ── 10 ── */}
        <h2 className="guide-h2">10 — Auto-Convert (Swapback)</h2>
        <p className="guide-p">Auto-convert makes the contract automatically swap accumulated tax tokens through a DEX and distribute the result. It is <strong>required</strong> if you set a rewards token, are doing a buyback, or have auto-LP enabled.</p>
        <div className="guide-steps">
          <Step n={1} title="Enable auto-convert">Check the box. The remaining fields appear once enabled. If you filled in a rewards token, this should always be enabled.</Step>
          <Step n={2} title="Payout mode (shown automatically)">
            <p className="guide-p">The form shows the current payout mode based on whether you filled in a rewards token:</p>
            <table className="guide-table" style={{ marginTop: 8 }}>
              <tbody>
                <tr>
                  <td style={{ color: "#d4a843", fontFamily: "monospace", fontSize: 13 }}>ERC20 Rewards Token</td>
                  <td>Rewards token field has a value — tax swaps to that ERC20 and distributes</td>
                </tr>
                <tr>
                  <td style={{ color: "#d4a843", fontFamily: "monospace", fontSize: 13 }}>Native BESC</td>
                  <td>No rewards token set — tax swaps to BESC (or WBESC) and distributes</td>
                </tr>
              </tbody>
            </table>
          </Step>
          <Step n={3} title="DEX Router on BESC">The Uniswap V2-compatible router address the contract uses to perform swaps. This is BescSwap's router or an equivalent AMM on BESC. Get this address from the BescSwap documentation.</Step>
          <Step n={4} title="Output Token (native BESC payout only)">
            <p className="guide-p">Only shown when payout mode is Native BESC (no rewards token set). Enter the WBESC contract address, or the address of whatever token you want to buyback.</p>
            <div className="guide-callout guide-callout--info">If you entered a rewards token above, this field is hidden — the rewards token contract IS the output of the swap.</div>
          </Step>
          <Step n={5} title="Trigger Threshold">
            <p className="guide-p">Minimum accumulated tax tokens in the contract before the auto-convert fires. Whole token units, not wei. Set this high enough it doesn't trigger on every small trade, but low enough that rewards distribute regularly.</p>
            <table className="guide-table" style={{ marginTop: 8 }}>
              <thead><tr><th>Token supply size</th><th>Suggested threshold</th></tr></thead>
              <tbody>
                <tr><td>Low supply (&lt; 1M tokens)</td><td><Code>100</Code> – <Code>1,000</Code></td></tr>
                <tr><td>Mid supply (1M – 1B)</td><td><Code>1,000</Code> – <Code>10,000</Code></td></tr>
                <tr><td>High supply (&gt; 1B)</td><td><Code>100,000</Code> – <Code>1,000,000</Code></td></tr>
              </tbody>
            </table>
            <div className="guide-callout guide-callout--danger">Do not leave this at 0. The contract will reject the deploy with a zero threshold when swapback is enabled.</div>
          </Step>
        </div>

        {/* ── 11 ── */}
        <h2 className="guide-h2">11 — Bridge Fee & Notes</h2>
        <div className="guide-card guide-card--block">
          <div className="guide-card-label">Bridge Fee (bps)</div>
          <div className="guide-card-val">Fee charged on each bridge transaction. 10 bps = 0.10%. Default is <Code>10</Code>. Max is <Code>1000</Code> (10%). Goes to the bridge treasury — separate from any token-level tax.</div>
        </div>
        <div className="guide-card guide-card--block" style={{ marginTop: 8 }}>
          <div className="guide-card-label">Notes (internal)</div>
          <div className="guide-card-val">Optional free text. Not shown publicly. Useful for logging the project contact, listing date, or any special instructions for this token.</div>
        </div>

        {/* ── 12 ── */}
        <h2 className="guide-h2">12 — After Deploy</h2>
        <div className="guide-steps">
          <Step n={1} title="Copy the contract address">A box appears after a successful deploy showing the new BESC wrapped token address. Copy and save it — share it with the project team.</Step>
          <Step n={2} title="Enable the token">New tokens are disabled by default. Find it in the token list and click <strong>Enable</strong>. The bridge goes live immediately.</Step>
          <Step n={3} title="Register AMM pairs (if buy/sell tax is set)">
            <p className="guide-p">If you configured separate buy and sell taxes, the contract needs to know which DEX pairs are "AMM pairs" so it can distinguish buys from sells. Without this step, buy/sell taxes won't work correctly.</p>
            <p className="guide-p">Open the token in <strong>⚙ Manage</strong> → AMM Pair Registration → enter each liquidity pool address for this token on BescSwap (or whichever DEX it lists on).</p>
          </Step>
          <Step n={4} title="Test a small bridge">Bridge a small test amount yourself before announcing it's live. Confirm it completes and the fee deducted matches the configured bps.</Step>
          <Step n={5} title="Ongoing management via Manage view">
            <p className="guide-p">After deploy you can update settings at any time without redeploying. Open any token's <strong>⚙ Manage</strong> view to:</p>
            <ul className="guide-list">
              <li>Update tax rates (transfer, buy, sell)</li>
              <li>Change swapback settings (on/off, router, output token, threshold)</li>
              <li>Update LP config (paired token, LP bps)</li>
              <li>Add or remove AMM pairs</li>
              <li>View on-chain stats (supply, swapback status, LP tax share)</li>
            </ul>
          </Step>
        </div>

        <div className="guide-divider" />

        <h2 className="guide-h2">Quick Reference — Why Deploys Fail</h2>
        <table className="guide-table">
          <thead><tr><th>Error</th><th>Fix</th></tr></thead>
          <tbody>
            {[
              ["Tax recipients don't sum to 100%", "Add up all recipient shares — they must total exactly 100."],
              ["Swapback threshold is 0", "Enter a number greater than 0 in the Trigger Threshold field."],
              ["ERC20 mode, no rewards token address", "Fill in the Rewards Token field with a valid 0x address on BESC."],
              ["Invalid source address format", "Check the address matches the format for the selected source chain (Section 05)."],
              ["Symbol already taken", "A token with that symbol is already registered. Use a different ticker or check the token list for duplicates."],
              ["LP tax > 0 but no LP paired token", "LP Paired Token address is required when Auto-LP share % is non-zero."],
              ["LP paired token is not a valid address", "Must be a 0x EVM address on BESC — confirm the paired token (e.g. WBESC) contract address."],
              ["Swapback enabled but no router set", "Enter the DEX router address in the Router field under Auto-Convert."],
            ].map(([err, fix]) => (
              <tr key={err}>
                <td style={{ color: "#e05c5c", fontFamily: "monospace", fontSize: 12.5, lineHeight: 1.4 }}>{err}</td>
                <td style={{ fontSize: 14 }}>{fix}</td>
              </tr>
            ))}
          </tbody>
        </table>

      </div>
    </div>
  );
}

function Step({ n, title, children }) {
  return (
    <div className="guide-step">
      <div className="guide-step-rail">
        <div className="guide-step-num">{n}</div>
        <div className="guide-step-line" />
      </div>
      <div className="guide-step-body">
        <div className="guide-step-title">{title}</div>
        {typeof children === "string" ? <p className="guide-p">{children}</p> : children}
      </div>
    </div>
  );
}

function Code({ children }) {
  return <code className="guide-code">{children}</code>;
}

function Tr({ name, req, opt, children }) {
  return (
    <tr>
      <td style={{ whiteSpace: "nowrap", verticalAlign: "top", paddingTop: 12 }}>
        <span className="guide-field-name">{name}</span>
        {req && <span className="guide-badge guide-badge--req">Req</span>}
        {opt && <span className="guide-badge guide-badge--opt">Opt</span>}
      </td>
      <td>{children}</td>
    </tr>
  );
}

function GuideStyles() {
  return (
    <style>{`
      .guide-wrap {
        min-height: 80vh;
        padding: 48px 20px 100px;
        display: flex;
        justify-content: center;
      }
      .guide-doc { width: 100%; max-width: 780px; }

      .guide-header { border-bottom: 1px solid rgba(255,214,102,0.12); padding-bottom: 28px; margin-bottom: 36px; }
      .guide-eyebrow { font-family: monospace; font-size: 11px; font-weight: 600; letter-spacing: .12em; text-transform: uppercase; color: #d4a843; margin-bottom: 10px; }
      .guide-h1 { font-size: 26px; font-weight: 700; color: #f0eadb; margin-bottom: 6px; line-height: 1.3; }
      .guide-lead { font-size: 13px; color: #7a8b9a; }

      .guide-toc { background: rgba(12,16,28,0.7); border: 1px solid rgba(255,214,102,0.12); border-radius: 12px; padding: 20px 22px; margin-bottom: 40px; }
      .guide-toc-title { font-family: monospace; font-size: 11px; font-weight: 700; letter-spacing: .12em; text-transform: uppercase; color: #7a8b9a; margin-bottom: 12px; }
      .guide-toc-list { list-style: none; display: flex; flex-direction: column; gap: 7px; }
      .guide-toc-list li { display: flex; align-items: baseline; gap: 10px; font-size: 14px; color: #c0cad8; }
      .guide-toc-n { font-family: monospace; font-size: 11px; color: #3a4a62; flex-shrink: 0; }

      .guide-h2 { font-family: monospace; font-size: 11px; font-weight: 700; letter-spacing: .1em; text-transform: uppercase; color: #d4a843; margin: 44px 0 18px; padding-bottom: 8px; border-bottom: 1px solid rgba(255,214,102,0.12); }
      .guide-p { font-size: 14px; color: #b8c4d4; line-height: 1.7; margin-bottom: 10px; }
      .guide-p:last-child { margin-bottom: 0; }
      .guide-p strong { color: #dde4f0; font-weight: 600; }

      .guide-list { margin: 8px 0 0 20px; display: flex; flex-direction: column; gap: 4px; }
      .guide-list li { font-size: 14px; color: #b8c4d4; line-height: 1.6; }

      .guide-grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin: 14px 0; }
      @media (max-width: 520px) { .guide-grid2 { grid-template-columns: 1fr; } }
      .guide-card { background: rgba(12,16,28,0.7); border: 1px solid rgba(255,255,255,0.07); border-radius: 9px; padding: 14px 16px; }
      .guide-card--block { margin: 6px 0; }
      .guide-card-label { font-family: monospace; font-size: 10px; font-weight: 600; letter-spacing: .1em; text-transform: uppercase; color: #7a8b9a; margin-bottom: 5px; }
      .guide-card-val { font-size: 14px; color: #b8c4d4; line-height: 1.55; }
      .guide-card-val strong { color: #dde4f0; }

      .guide-callout { border-radius: 8px; padding: 11px 14px; margin: 12px 0; font-size: 13.5px; line-height: 1.6; color: #dde4f0; }
      .guide-callout strong { font-weight: 600; color: #dde4f0; }
      .guide-callout--tip    { background: rgba(78,203,113,0.08);  border: 1px solid rgba(78,203,113,0.3); }
      .guide-callout--warn   { background: rgba(224,147,60,0.08);  border: 1px solid rgba(224,147,60,0.3); }
      .guide-callout--info   { background: rgba(212,168,67,0.07);  border: 1px solid rgba(212,168,67,0.2); }
      .guide-callout--danger { background: rgba(224,92,92,0.08);   border: 1px solid rgba(224,92,92,0.3); }

      .guide-highlight { background: rgba(212,168,67,0.07); border: 1px solid rgba(212,168,67,0.22); border-radius: 10px; padding: 16px 18px; margin: 14px 0; }
      .guide-highlight-title { font-size: 15px; font-weight: 700; color: #d4a843; margin-bottom: 4px; }

      /* Scenario cards */
      .guide-scenario { background: rgba(12,16,28,0.6); border: 1px solid rgba(255,255,255,0.08); border-radius: 11px; padding: 18px 20px; margin: 14px 0; }
      .guide-scenario-badge { display: inline-block; font-family: monospace; font-size: 10px; font-weight: 700; letter-spacing: .1em; padding: 2px 8px; border-radius: 5px; margin-bottom: 8px; text-transform: uppercase; }
      .guide-scenario-badge--plain   { background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.14); color: #7a8b9a; }
      .guide-scenario-badge--tax     { background: rgba(224,147,60,0.12);  border: 1px solid rgba(224,147,60,0.3);  color: #e0933c; }
      .guide-scenario-badge--buyback { background: rgba(78,203,113,0.1);   border: 1px solid rgba(78,203,113,0.3);  color: #4ecb71; }
      .guide-scenario-badge--rewards { background: rgba(212,168,67,0.1);   border: 1px solid rgba(212,168,67,0.3);  color: #d4a843; }
      .guide-scenario-badge--lp      { background: rgba(120,160,240,0.1);  border: 1px solid rgba(120,160,240,0.3); color: #88a8f0; }
      .guide-scenario-badge--full    { background: rgba(200,100,220,0.1);  border: 1px solid rgba(200,100,220,0.3); color: #c878dc; }
      .guide-scenario-title { font-size: 16px; font-weight: 700; color: #dde4f0; margin-bottom: 8px; }
      .guide-scenario-fields { display: flex; flex-wrap: wrap; gap: 6px; margin: 10px 0; }
      .guide-field-pill { font-family: monospace; font-size: 11px; font-weight: 600; padding: 3px 9px; border-radius: 5px; }
      .guide-field-pill--req { background: rgba(212,168,67,0.08); border: 1px solid rgba(212,168,67,0.22); color: #d4a843; }
      .guide-example { background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.07); border-radius: 8px; padding: 12px 14px; margin-top: 10px; }
      .guide-example-label { font-family: monospace; font-size: 10px; font-weight: 600; letter-spacing: .1em; text-transform: uppercase; color: #7a8b9a; margin-bottom: 6px; }
      .guide-example-body { font-family: monospace; font-size: 12.5px; color: #b8c4d4; line-height: 1.8; }

      .guide-steps { display: flex; flex-direction: column; }
      .guide-step { display: grid; grid-template-columns: 44px 1fr; gap: 0 16px; }
      .guide-step-rail { display: flex; flex-direction: column; align-items: center; padding-top: 2px; }
      .guide-step-num { width: 30px; height: 30px; border-radius: 50%; background: rgba(12,16,28,0.9); border: 1.5px solid rgba(255,214,102,0.25); display: flex; align-items: center; justify-content: center; font-family: monospace; font-size: 12px; font-weight: 700; color: #d4a843; flex-shrink: 0; position: relative; z-index: 1; }
      .guide-step-line { width: 1px; flex: 1; background: rgba(255,255,255,0.07); min-height: 20px; margin: 3px 0; }
      .guide-step:last-child .guide-step-line { display: none; }
      .guide-step-body { padding-bottom: 28px; padding-top: 3px; }
      .guide-step-title { font-size: 15px; font-weight: 600; color: #dde4f0; margin-bottom: 6px; }

      .guide-chain-grid { display: flex; flex-wrap: wrap; gap: 8px; margin: 10px 0 14px; }
      .guide-chain-chip { background: rgba(12,16,28,0.8); border: 1px solid rgba(255,255,255,0.1); border-radius: 7px; padding: 7px 13px; font-family: monospace; font-size: 12px; display: flex; flex-direction: column; gap: 2px; }
      .guide-chain-name { color: #dde4f0; font-weight: 600; }
      .guide-chain-fmt  { color: #7a8b9a; font-size: 10px; }

      .guide-addr { font-family: monospace; font-size: 12px; background: rgba(12,16,28,0.8); border: 1px solid rgba(255,255,255,0.08); border-radius: 8px; padding: 12px 14px; margin: 8px 0; white-space: pre-wrap; word-break: break-all; color: #7a8b9a; line-height: 1.7; overflow-x: auto; }

      .guide-table { width: 100%; border-collapse: collapse; margin: 14px 0; font-size: 14px; }
      .guide-table th { font-family: monospace; font-size: 10px; font-weight: 600; letter-spacing: .08em; text-transform: uppercase; color: #7a8b9a; text-align: left; padding: 8px 12px; border-bottom: 1px solid rgba(255,255,255,0.08); }
      .guide-table td { padding: 10px 12px; border-bottom: 1px solid rgba(255,255,255,0.05); vertical-align: top; line-height: 1.55; color: #b8c4d4; }
      .guide-table tr:last-child td { border-bottom: none; }
      .guide-table tr:hover td { background: rgba(255,255,255,0.02); }
      .guide-field-name { font-family: monospace; font-size: 13px; font-weight: 500; color: #dde4f0; }
      .guide-badge { display: inline-block; font-family: monospace; font-size: 10px; font-weight: 600; letter-spacing: .06em; padding: 1px 6px; border-radius: 4px; margin-left: 6px; text-transform: uppercase; }
      .guide-badge--req { background: rgba(212,168,67,0.1); border: 1px solid rgba(212,168,67,0.25); color: #d4a843; }
      .guide-badge--opt { background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.1); color: #7a8b9a; }

      .guide-code { font-family: monospace; font-size: 12.5px; background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.1); padding: 1px 6px; border-radius: 4px; color: #dde4f0; }
      .guide-divider { border: none; border-top: 1px solid rgba(255,255,255,0.07); margin: 40px 0; }
    `}</style>
  );
}
